import { Inject, Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { Util } from 'services/util';
import { IXMOptions } from 'core/interfaces';
import { ApiChannel, CONFIG_TOKEN, StorageToken } from 'core/constants';
import { CimaToken } from '../cima/token';
import { LoggerService } from './logger';
import { SessionStorage } from 'core/services';

@Injectable()
export class ApiResourceInterceptor implements HttpInterceptor {
    private config: IXMOptions;
    private cimaToken: CimaToken;
    private inbentaSession: string = Util.generateUuid();
    private logger: LoggerService;
    private sessionStorage: SessionStorage;

    constructor(cimaToken: CimaToken, @Inject(CONFIG_TOKEN) config: IXMOptions, logger: LoggerService, sessionStorage: SessionStorage ) {
        Object.assign(this, { cimaToken, config, logger, sessionStorage });
    }

    // eslint-disable-next-line complexity
    public intercept(request: HttpRequest<object>, next: HttpHandler): Observable<HttpEvent<object>> {
        let newRequest: HttpRequest<object> = request;
        const startTime: number = performance.now();

        if (!request.url.startsWith('/')) {
            const message: string = `partial url must start with a /, given \'${request.url}\'`;
            /* eslint-disable no-console */
            console.error(message);
            /* eslint-enable no-console */

            this.logger.scriptError(message);

            return throwError(message);
        }

        const channelCode: string = request.headers.get('apiChannel');
        newRequest = newRequest.clone({ headers: request.headers.delete('apiChannel') }); // should not be used after this interceptor

        if (request.method !== 'GET') {
            newRequest = newRequest.clone({ setHeaders: { 'Content-Type': 'application/json' } });
        }

        switch (channelCode) {
            case ApiChannel.CIMA_WEB:
                newRequest = newRequest.clone({
                    url: `${this.config.CIMA_BASE_URL}${request.url}`,
                    setHeaders: { Authorization: `Basic ${this.config.CIMA_WEB}` }
                });
                break;

            case ApiChannel.CMS:
                newRequest = newRequest.clone({ url: `${this.config.URL_CMS}${request.url}` });
                break;

            case ApiChannel.CMS_LIVE:
                if (this.config.CMS_LIVE) {
                    newRequest = newRequest.clone({
                        url: `${this.config.CMS_LIVE}${request.url}`,
                        setParams: {
                            env: this.config.CMS_ENV,
                            contentId: request.url,
                            cdsid: this.config.CMS_CDSID
                        }
                    });
                } else {
                    newRequest = newRequest.clone({ url: `${this.config.URL_CMS}${request.url}` });
                }
                break;

            case ApiChannel.MOCKABLE:
                newRequest = newRequest.clone({
                    url: `${this.config.TEST_API_URL}${request.url}`,
                    setHeaders: {
                        'Content-Type': 'application/json'
                    }
                });
                break;

            case ApiChannel.GATEWAY:
                newRequest = newRequest.clone({
                    url: `${this.config.GATEWAY_BASE_URL}${request.url}`,
                    setHeaders: {
                        Authorization: `Bearer ${this.cimaToken.accessToken}`,
                        'X-Channel': 'WEB',
                        'X-global-tracking-id': Util.generateUuid()
                    }
                });

                if (this.sessionStorage.get(StorageToken.CIMA_USER_ACCOUNT_GUID)) {
                    newRequest = newRequest.clone({ setHeaders: { 'CIMA-User-Account-GUID': this.sessionStorage.get(StorageToken.CIMA_USER_ACCOUNT_GUID) } });
                }

                if (this.sessionStorage.get(StorageToken.VERIFICATION_REFERENCE_NUMBER) && request.url?.includes('activate')) {
                    newRequest = newRequest.clone({ setHeaders: { 'X-VerificationReferenceNumber': this.sessionStorage.get(StorageToken.VERIFICATION_REFERENCE_NUMBER) } });
                }

                if (this.sessionStorage.get(StorageToken.X_TENANT_ID)) {
                    newRequest = newRequest.clone({ setHeaders: { 'X-Tenant-Id': this.sessionStorage.get(StorageToken.X_TENANT_ID) } });
                }
                break;
            
            case ApiChannel.PROXY_GATEWAY:
                newRequest = newRequest.clone({
                    url: `${this.config.PROXY_GATEWAY_BASE_URL}${request.url}`,
                    setHeaders: {
                        Authorization: `Bearer ${this.cimaToken.accessToken}`,
                        'X-Channel': 'WEB',
                        'X-global-tracking-id': Util.generateUuid()
                    }
                });
            
                break;
            
            case ApiChannel.PROXY_CMS:
                const baseUrl: string = ['pre-prod', 'prod'].includes(this.config.LOG_ENV) ? this.config.URL_CMS.replace(this.config.PROXY_BASE_CMS_URL, this.config.PROXY_URL_CMS) : this.config.URL_CMS;
                newRequest = newRequest.clone({ url: `${baseUrl}${request.url}` });
                
                break;

            case ApiChannel.PROXY_LOGGER:
                newRequest = newRequest.clone({
                    url: `${this.config.PROXY_LOGGER_URL}${request.url}`,
                    setHeaders: {
                        'Content-Type': 'application/json'
                    }
                });

                break;

            case ApiChannel.BROADBAND_LABELS:
                newRequest = newRequest.clone({ url: `${this.config.BROADBAND_LABELS_URL}${request.url}`,
                    setHeaders: {
                        'Content-Type': 'application/json-patch+json',
                        Authorization: `Bearer ${this.cimaToken.accessToken}`,
                        'tracking-id': Util.generateUuid16()
                    }
                });
                break;

            case ApiChannel.SSM_GATEWAY:
                newRequest = newRequest.clone({
                    url: `${this.config.SSM_GATEWAY_BASE_URL}${request.url}`,
                    setHeaders: {
                        Authorization: `Bearer ${this.cimaToken.accessToken}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (this.sessionStorage.get(StorageToken.CIMA_USER_ACCOUNT_GUID)) {
                    newRequest = newRequest.clone({ setHeaders: { 'CIMA-User-Account-GUID': this.sessionStorage.get(StorageToken.CIMA_USER_ACCOUNT_GUID) } });
                }

                if (this.sessionStorage.get(StorageToken.X_TENANT_ID)) {
                    newRequest = newRequest.clone({ setHeaders: { 'X-Tenant-Id': this.sessionStorage.get(StorageToken.X_TENANT_ID) } });
                }
                break;

            case ApiChannel.LOGGER:
                newRequest = newRequest.clone({
                    url: `${this.config.LOGGER_URL}${request.url}`,
                    setHeaders: {
                        'Content-Type': 'application/json'
                    }
                });

                if (this.sessionStorage.get(StorageToken.CIMA_USER_ACCOUNT_GUID)) {
                    newRequest = newRequest.clone({ setHeaders: { 'CIMA-User-Account-GUID': this.sessionStorage.get(StorageToken.CIMA_USER_ACCOUNT_GUID) } });
                }
                break;

            case ApiChannel.PCAT:
                newRequest = newRequest.clone({ url: `${this.config.PCAT_BASE_URL}${request.url}` });
                break;

            case ApiChannel.PROXY:
                newRequest = newRequest.clone({ url: `${this.config.MSP_PROXY}${request.url}` });
                break;

            case ApiChannel.AKAMAI_PROXY:
                newRequest = this.appendAkamaiProxyHeader(newRequest.clone({ url: `${this.config.AKAMAI_PROXY}${request.url}` }));
                break;

            case ApiChannel.INBENTA:
                newRequest = newRequest.clone({
                    url: `${this.config.MSP_PROXY}${request.url}`,
                    setHeaders: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    },
                    setParams: {
                        session_id: this.inbentaSession
                    }
                });
                break;

            case ApiChannel.XAPI:
                newRequest = newRequest.clone({ url: `${this.config.XAPI}${request.url}` });
                break;

            default:
                const message: string = 'missing HTTP Header \'apiChannel\'';
                /* eslint-disable no-console */
                console.error(message);
                /* eslint-enable no-console */

                this.logger.scriptError(message);

                return throwError(message);
        }

        // everyone accepts JSON
        newRequest = newRequest.clone({ setHeaders: { Accept: 'application/json' } });

        return next.handle(newRequest).pipe(map((httpEvent: HttpEvent<object>) => {
            if (httpEvent instanceof HttpResponse
                && channelCode !== ApiChannel.LOGGER
                && channelCode !== ApiChannel.PROXY_LOGGER
                && channelCode !== ApiChannel.CMS_LIVE
                && channelCode !== ApiChannel.PROXY_CMS
                && channelCode !== ApiChannel.CMS) {
                this.logger.info(httpEvent, newRequest, startTime);
            }

            if (httpEvent instanceof HttpResponse && (channelCode === ApiChannel.CMS || channelCode === ApiChannel.PROXY_CMS || channelCode === ApiChannel.CMS_LIVE)) {
                this.logger.cmsInfo(httpEvent, newRequest, startTime);
            }

            return httpEvent;
        }));
    }

    private appendAkamaiProxyHeader(newRequest: HttpRequest<object>): HttpRequest<object> {
        if (this.config.AKAMAI_PROXY_HEADER_VALUE) {
            return newRequest.clone({
                setHeaders: {
                    'XM-TRAFFIC': this.config.AKAMAI_PROXY_HEADER_VALUE
                }
            });
        }

        return newRequest;
    }
}
